import { Component } from 'react'

/**
 * Embed-iframe error boundary. Without this, a single render-time exception
 * unmounts the whole tree and leaves customers staring at a white page,
 * which is exactly what we hit on /embed/reseller/* today.
 *
 * Now: shows the error message + component stack so the operator (or our
 * own debug session) can see what crashed without having to crack open
 * minified source maps. Includes a "Try again" button that re-mounts the
 * tree so the customer can recover without a hard refresh.
 */
export default class EmbedErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null, info: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // Persist for our own debugging — also log to the console so prod
    // DevTools picks it up without source maps.
    console.error('[EmbedErrorBoundary]', error, info)
    this.setState({ info })
  }

  reset = () => this.setState({ error: null, info: null })

  render() {
    if (!this.state.error) return this.props.children

    const err = this.state.error
    const stack = (this.state.info?.componentStack || '').trim()

    return (
      <div style={{
        padding: '32px 24px',
        maxWidth: 720,
        margin: '40px auto',
        background: '#fff',
        border: '1px solid #fecaca',
        borderRadius: 12,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#b91c1c', marginBottom: 8 }}>
          Something went wrong loading this page.
        </div>
        <p style={{ color: '#475569', fontSize: 14, lineHeight: 1.5, marginTop: 0 }}>
          We've logged the issue. You can try again, or refresh the page.
        </p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button onClick={this.reset} style={{
            background: '#0f172a', color: '#fff', border: 'none',
            padding: '10px 18px', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
          }}>Try again</button>
          <button onClick={() => window.location.reload()} style={{
            background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1',
            padding: '10px 18px', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
          }}>Reload page</button>
        </div>

        {/* Developer details. Always visible in the embed — the iframe is
            on stayplay.integolf.com, not a customer-branded page, so the
            information leak is bounded. If we later want to hide it from
            customers we can gate on hostname or NODE_ENV. */}
        <details style={{ marginTop: 12, fontSize: 12, color: '#64748b' }}>
          <summary style={{ cursor: 'pointer', userSelect: 'none', fontWeight: 600 }}>
            Show details (for support)
          </summary>
          <div style={{ marginTop: 8, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 }}>
            <div style={{ marginBottom: 8 }}>
              <strong style={{ color: '#0f172a' }}>{err.name || 'Error'}:</strong>{' '}
              <code style={{ color: '#b91c1c', wordBreak: 'break-word' }}>{err.message}</code>
            </div>
            {err.stack && (
              <pre style={{
                margin: 0, padding: 8, background: '#0f172a', color: '#86efac',
                fontSize: 11, lineHeight: 1.4, overflow: 'auto', borderRadius: 4,
                maxHeight: 200, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>{err.stack}</pre>
            )}
            {stack && (
              <>
                <div style={{ marginTop: 8, fontWeight: 600, color: '#0f172a' }}>Component stack:</div>
                <pre style={{
                  margin: 0, padding: 8, background: '#0f172a', color: '#7dd3fc',
                  fontSize: 11, lineHeight: 1.4, overflow: 'auto', borderRadius: 4,
                  maxHeight: 200, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>{stack}</pre>
              </>
            )}
          </div>
        </details>
      </div>
    )
  }
}
